import React, {useMemo, useState} from 'react';
import {ChevronDown, ChevronUp} from 'react-bootstrap-icons';
import c from './subscriptionPlans.module.css';

/**
 * Active subscriptions, one card per plan rather than one table row per
 * endpoint.
 *
 * The backend returns a row per endpoint, so a 34-endpoint monthly plan used to
 * render as 34 rows whose start date, end date and balance were identical -
 * three of the four columns repeating the same value, spread over four pages of
 * pagination. Rows that share a start date, end date and balance are the same
 * purchase, so they collapse into one card and the endpoint list moves behind a
 * toggle.
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAY_MS = 24 * 60 * 60 * 1000;

// 'YYYY-MM-DD' -> local midnight. new Date('2026-06-19') is parsed as UTC, which
// renders as the 18th for anyone west of Greenwich.
const parseDay = (value) => {
    if (!value) return null;
    const [y, m, d] = String(value).split('-').map(Number);
    return (y && m && d) ? new Date(y, m - 1, d) : null;
};

const formatDay = (value) => {
    const d = parseDay(value);
    return d ? `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}` : value;
};

const daysUntil = (value) => {
    const end = parseDay(value);
    if (!end) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    // round rather than floor: a DST change makes the difference a non-integer
    // number of days
    return Math.round((end - today) / DAY_MS);
};

// Spelled out so nobody has to subtract two dates in their head.
const remainingLabel = (days) => {
    if (days === null) return null;
    if (days < 0) return 'expired';
    if (days === 0) return 'expires today';
    if (days === 1) return '1 day left';
    if (days < 60) return `${days} days left`;
    return `${Math.round(days / 30.44)} months left`;
};

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** Rows of one purchase, grouped by the assay group they belong to. */
const byGroup = (items) => {
    const groups = new Map();
    items.forEach(item => {
        const name = item.group_name || 'Other';
        if (!groups.has(name)) groups.set(name, []);
        groups.get(name).push(item);
    });
    return [...groups.entries()];
};

const PlanCard = ({plan}) => {
    const [open, setOpen] = useState(false);

    const {items, pending, compounds_left: left, start_date, end_date} = plan;
    const groups = useMemo(() => byGroup(items), [items]);
    const days = pending ? null : daysUntil(end_date);
    const expiring = days !== null && days <= 30;

    const title = pending ? 'Queued'
        : left === null || left === undefined ? 'Monthly plan'
        : 'Compound credits';

    const badge = left === null || left === undefined
        ? 'Unlimited compounds'
        : `${plural(left, 'compound')} left`;

    // One endpoint needs no list and no toggle - name it on the card instead.
    const single = items.length === 1;

    const meta = pending
        ? 'Paid. Activates automatically when your current plan expires.'
        : [single ? items[0].assay_name : null,
           start_date && end_date ? `${formatDay(start_date)} → ${formatDay(end_date)}` : null,
           remainingLabel(days)].filter(Boolean).join(' · ');

    return (
        <div className={`${c.card}${pending ? ' ' + c.cardPending : ''}`}>
            <div className={c.cardTop}>
                <div className={c.cardHeadings}>
                    <p className={c.cardTitle}>{title}</p>
                    <p className={`${c.cardMeta}${expiring ? ' ' + c.cardMetaWarn : ''}`}>{meta}</p>
                </div>
                <span className={`${c.badge}${left === null || left === undefined
                    ? ' ' + c.badgeUnlimited : ''}`}>{badge}</span>
            </div>

            {!single && (
                <div className={c.cardFoot}>
                    <span className={c.endpointCount}>{plural(items.length, 'endpoint')}</span>
                    {/* group chips give the shape of the plan without the full list */}
                    {groups.slice(0, 3).map(([name, rows]) => (
                        <span key={name} className={c.chip}>{name} {rows.length}</span>
                    ))}
                    {groups.length > 3 && <span className={c.chip}>+{groups.length - 3} more</span>}
                    <button type='button' className={c.toggle} onClick={() => setOpen(!open)}
                            aria-expanded={open}>
                        {open ? 'Hide endpoints' : 'Show endpoints'}
                        {open ? <ChevronUp size={12}/> : <ChevronDown size={12}/>}
                    </button>
                </div>
            )}

            {!single && open && (
                <div className={c.endpointList}>
                    {groups.map(([name, rows]) => (
                        <div key={name} className={c.endpointGroup}>
                            <p className={c.endpointGroupName}>{name}</p>
                            <ul className={c.endpointNames}>
                                {rows.map((row, i) => <li key={i}>{row.assay_name}</li>)}
                            </ul>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const SubscriptionPlans = ({subscriptions = []}) => {
    const plans = useMemo(() => {
        const grouped = new Map();

        subscriptions.forEach(row => {
            // A shared start date, end date and balance means one purchase. Fall
            // back to the old shape - no `pending` flag, dates absent on the
            // queued rows - so an older backend still renders.
            const pending = row.pending ?? (!row.start_date && !row.end_date);
            // Queued rows carry no dates, so they group on the balance alone -
            // enough to keep a queued monthly plan separate from queued credits.
            const key = pending
                ? `pending|${row.compounds_left}`
                : `${row.start_date}|${row.end_date}|${row.compounds_left}`;

            if (!grouped.has(key)) {
                grouped.set(key, {
                    key, pending,
                    start_date: row.start_date,
                    end_date: row.end_date,
                    compounds_left: row.compounds_left,
                    items: [],
                });
            }
            grouped.get(key).items.push(row);
        });

        // Queued last; among the active ones, unmetered plans first, then the
        // largest balance - the biggest entitlement is what people look for.
        return [...grouped.values()].sort((a, b) => {
            if (a.pending !== b.pending) return a.pending ? 1 : -1;
            const av = a.compounds_left ?? Infinity;
            const bv = b.compounds_left ?? Infinity;
            return bv - av;
        });
    }, [subscriptions]);

    return (
        <div className={c.plans}>
            {plans.map(plan => <PlanCard key={plan.key} plan={plan}/>)}
        </div>
    );
};

export default SubscriptionPlans;
